/**
 * Insight construction for the ambient intelligence bar.
 *
 * SPLIT OUT OF THE COMPONENT so it can be tested directly. The defect this
 * guards (a 200 with a partial body throwing) is invisible from the rendered
 * component: the throw escapes to the root, so there is nothing left to assert
 * against. Keeping it here also satisfies react-refresh/only-export-components
 * -- a file that exports a component may export nothing else, and this file
 * exports no component.
 */
import {
  AlertTriangle,
  Brain,
  ListChecks,
  Shield,
  Sparkles,
  Users,
  Zap,
} from "lucide-react";

import type { FelixBriefing } from "@/api/services";

export interface Insight {
  id: string;
  icon: React.ElementType;
  iconColor: string;
  bgColor: string;
  message: string;
  action?: { label: string; href?: string; onClick?: () => void };
  priority: "critical" | "high" | "medium" | "low";
}

/** What a needs-attention bucket is called, singular and plural. The bar
 *  read "1 item need attention — most in pending action items" on the ETS
 *  screenshots (row 8660d501): a verb that did not agree and a raw key. */
const ATTENTION_NOUNS: Record<string, [string, string]> = {
  pending_action_items: ["action item", "action items"],
  critical_conflicts: ["critical conflict", "critical conflicts"],
  uncovered_requirements: ["requirement without test coverage", "requirements without test coverage"],
  stale_tasks: ["task with no recent update", "tasks with no recent update"],
  awaiting_validation: ["item awaiting validation", "items awaiting validation"],
  upcoming_meetings: ["upcoming meeting", "upcoming meetings"],
};

function attentionNoun(key: string, n: number): string {
  const pair = ATTENTION_NOUNS[key];
  if (pair) return n === 1 ? pair[0] : pair[1];
  const words = key.replace(/_/g, " ");
  return n === 1 ? words.replace(/s$/, "") : words;
}

export function attentionMessage(total: number, topKey: string | undefined, bucketCount: number): string {
  if (topKey && bucketCount === 1) {
    return `${total} ${attentionNoun(topKey, total)} ${total === 1 ? "needs" : "need"} your attention`;
  }
  const head = `${total} item${total === 1 ? "" : "s"} ${total === 1 ? "needs" : "need"} your attention`;
  return topKey ? `${head}, most of them ${attentionNoun(topKey, 2)}` : head;
}

export function buildInsights(
  briefing: FelixBriefing | undefined,
  pathname: string,
  presenting = false,
): Insight[] {
  // Presenting: the person's own work never rotates onto a shared screen, on any page (founder
  // 6441608339 item 1: "Help me with my task: Confirm prod rate limit on + rotate secrets…" on /skills).
  if (presenting) return [];
  // SHAPE, not just presence. The old guard was `if (!briefing) return []`,
  // which covers a missing briefing and nothing else -- and every read below
  // then dereferenced two levels down: my_work.open_tasks.filter,
  // Object.values(needs_attention), for..of nudges,
  // suggested_actions.slice, since_last_visit.team_actions.
  //
  // A 200 carrying a PARTIAL body therefore threw, and this component is
  // mounted in AppShell OUTSIDE the ErrorBoundary that wraps FelixChat and the
  // page children -- so the throw reached the root and blanked the whole shell
  // rather than the one bar. Normalising here is half the fix; the other half
  // is the boundary added around the mount, so a future field can only cost
  // this feature.
  if (!briefing || typeof briefing !== "object") return [];
  // NOT ON A CLIENT'S SCREEN (founder 6415500288 item 4, 6419537313 item 3).
  // This briefing is the PERSON'S, fetched with no project: every rotation is
  // about their work across the org -- high-priority tasks on any project,
  // FTL's own feedback queue, "Help me with my task: <another project's
  // task>", org-wide stale counts. On a project or meeting page -- what a
  // client sees over a screen share -- none of it belongs, so the bar shows
  // nothing there. Dropping only the awaiting-validation bucket left the
  // other rotations on screen.
  if (pathname.startsWith("/projects/") || pathname.startsWith("/meeting")) return [];
  const myWork = briefing.my_work ?? { open_tasks: [], open_task_count: 0 };
  const openTasks = Array.isArray(myWork.open_tasks) ? myWork.open_tasks : [];
  const openTaskCount =
    typeof myWork.open_task_count === "number" ? myWork.open_task_count : 0;
  const needsAttention: Record<string, number> =
    briefing.needs_attention && typeof briefing.needs_attention === "object"
      ? briefing.needs_attention
      : {};
  const nudges = Array.isArray(briefing.nudges) ? briefing.nudges : [];
  const suggestedActions = Array.isArray(briefing.suggested_actions)
    ? briefing.suggested_actions
    : [];
  const sinceLastVisit = briefing.since_last_visit ?? {
    team_actions: 0,
    completed_tasks: 0,
  };
  const teamActions =
    typeof sinceLastVisit.team_actions === "number"
      ? sinceLastVisit.team_actions
      : 0;
  const completedTasks =
    typeof sinceLastVisit.completed_tasks === "number"
      ? sinceLastVisit.completed_tasks
      : 0;
  const insights: Insight[] = [];

  // Overdue / blocked tasks
  const overdueTasks = openTasks.filter(
    (t) => t.priority === "CRITICAL" || t.priority === "HIGH",
  );
  if (overdueTasks.length > 0) {
    insights.push({
      id: "high-priority-tasks",
      icon: AlertTriangle,
      iconColor: "text-amber-500",
      bgColor: "bg-amber-500/10",
      message: `${overdueTasks.length} high-priority task${overdueTasks.length > 1 ? "s" : ""} need${overdueTasks.length === 1 ? "s" : ""} your attention`,
      action: { label: "View Tasks", href: "/tasks" },
      priority: "high",
    });
  }

  // Needs attention items
  const attentionTotal = Object.values(needsAttention).reduce((a, b) => a + b, 0);
  if (attentionTotal > 0) {
    const buckets = Object.entries(needsAttention).filter(([, n]) => n > 0);
    const topKey = buckets.sort(([, a], [, b]) => b - a)[0]?.[0];
    insights.push({
      id: "attention-needed",
      icon: Shield,
      iconColor: "text-red-500",
      bgColor: "bg-red-500/10",
      message: attentionMessage(attentionTotal, topKey, buckets.length),
      action: { label: "Review", href: "/dashboard" },
      priority: "critical",
    });
  }

  // Nudges from Felix
  for (const nudge of nudges) {
    insights.push({
      id: `nudge-${nudge.type}`,
      icon: nudge.priority === "high" ? Zap : nudge.priority === "medium" ? Brain : Sparkles,
      iconColor: nudge.priority === "high" ? "text-orange-500" : nudge.priority === "medium" ? "text-blue-500" : "text-purple-500",
      bgColor: nudge.priority === "high" ? "bg-orange-500/10" : nudge.priority === "medium" ? "bg-blue-500/10" : "bg-purple-500/10",
      message: nudge.message,
      priority: nudge.priority === "high" ? "high" : nudge.priority === "medium" ? "medium" : "low",
    });
  }

  // Suggested actions
  for (const suggestion of suggestedActions.slice(0, 2)) {
    insights.push({
      id: `suggest-${suggestion.label}`,
      icon: Sparkles,
      iconColor: "text-brand-500",
      bgColor: "bg-brand-500/10",
      message: suggestion.chip_text,
      priority: suggestion.priority === "high" ? "high" : "medium",
    });
  }

  // Since last visit highlights
  if (teamActions > 0) {
    insights.push({
      id: "since-last-visit",
      icon: Users,
      iconColor: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
      message: `${teamActions} team action${teamActions > 1 ? "s" : ""} since you were last here${completedTasks > 0 ? ` — ${completedTasks} task${completedTasks > 1 ? "s" : ""} completed` : ""}`,
      priority: "low",
    });
  }

  // Open task count
  if (openTaskCount > 0 && !overdueTasks.length) {
    insights.push({
      id: "open-tasks",
      icon: ListChecks,
      iconColor: "text-teal-500",
      bgColor: "bg-teal-500/10",
      message: `You have ${openTaskCount} open task${openTaskCount > 1 ? "s" : ""} across your projects`,
      action: { label: "View My Work", href: "/tasks" },
      priority: "low",
    });
  }

  // Sort by priority
  const order = { critical: 0, high: 1, medium: 2, low: 3 };
  return insights.sort((a, b) => order[a.priority] - order[b.priority]);
}

// ── Main Component ─────────────────────────────────────────────────

